import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const LanguageContext = createContext();

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const availableLanguages = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  const [currentLanguage, setCurrentLanguageState] = useState('nl');

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = () => {
      // 1. Check URL parameter
      const searchParams = new URLSearchParams(location.search);
      const urlLang = searchParams.get('lang');

      if (urlLang && availableLanguages.some(l => l.code === urlLang)) {
        setLanguage(urlLang);
        return;
      }

      // 2. Check localStorage
      const storedLang = localStorage.getItem('preferred_language');
      if (storedLang && availableLanguages.some(l => l.code === storedLang)) {
        setLanguage(storedLang);
        return;
      }

      // 3. Check browser language
      const browserLang = navigator.language.split('-')[0]; // 'nl-BE' -> 'nl'
      if (availableLanguages.some(l => l.code === browserLang)) {
        setLanguage(browserLang);
        return;
      }

      // 4. Default to Dutch
      setLanguage('nl');
    };

    initializeLanguage();
  }, []); // Only run on mount

  const setLanguage = (langCode) => {
    if (!availableLanguages.some(l => l.code === langCode)) {
      console.error(`Invalid language code: ${langCode}`);
      return;
    }

    // Update state
    setCurrentLanguageState(langCode);

    // Update i18next
    i18n.changeLanguage(langCode);

    // Update localStorage
    localStorage.setItem('preferred_language', langCode);

    // Update URL parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', langCode);

    // Use navigate to update URL without page reload
    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`
    }, { replace: true });
  };

  const value = {
    currentLanguage,
    setLanguage,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
